import { XIcon } from '@phosphor-icons/react'
import { DialogClose, DialogTitle } from '@radix-ui/react-dialog'
import clsx from 'clsx'
import { FahrlehrerCh, MediaResponse } from '@/src/@types/blinkadmin'
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@/src/components/core/Dialog'
import Picture from '@/src/components/elements/Picture'
import Container from '@/src/components/layout/Container'

const LayeredImage = ({
  image,
  sizes,
}: {
  image: MediaResponse
  sizes: string
}) => (
  <>
    <Picture
      permalink={image.url}
      fill
      alt=""
      aria-hidden
      className="scale-110 object-cover blur-3xl"
      sizes={sizes}
    />
    <Picture
      permalink={image.url}
      fill
      alt={image.description ?? ''}
      className="object-contain"
      sizes={sizes}
    />
  </>
)

const GalleryImage = ({
  image,
  sizes,
  className,
}: {
  image: MediaResponse
  sizes: string
  className?: string
}) => (
  <Dialog>
    <DialogTrigger
      className={clsx(
        'relative block w-full cursor-pointer overflow-hidden rounded-lg bg-gray-150',
        className
      )}
    >
      <LayeredImage image={image} sizes={sizes} />
      <span className="sr-only">Bild vergrössern</span>
    </DialogTrigger>
    <DialogContent
      withPositioning={false}
      className="inset-0 w-auto overflow-hidden md:inset-x-10 md:top-9.5 md:bottom-10 md:rounded-2xl"
    >
      <DialogTitle className="sr-only">
        {image.description ?? 'Bild'}
      </DialogTitle>
      <DialogClose className="absolute top-3 right-3 z-50 cursor-pointer rounded-full bg-black/20 p-2">
        <XIcon weight="bold" size={24} className="text-white" />
        <span className="sr-only">Schliessen</span>
      </DialogClose>
      <div className="relative size-full">
        <LayeredImage image={image} sizes="100vw" />
      </div>
    </DialogContent>
  </Dialog>
)

const Images = ({ images }: FahrlehrerCh.TeacherProfile) => {
  const imgs = images.slice(0, 4)
  if (imgs.length === 0) return null

  const [img0, img1, img2, img3] = imgs

  return (
    <section id="images">
      <Container className="mt-20 md:mt-24">
        <h2 className="text-4xl font-black md:text-10xl">Bilder</h2>
        {imgs.length === 1 && (
          <GalleryImage
            image={img0}
            sizes="100vw"
            className="mt-6 h-70 md:h-120"
          />
        )}
        {imgs.length === 2 && (
          <div className="mt-6 grid grid-cols-1 gap-3 md:h-120 md:grid-cols-2 md:grid-rows-1">
            <GalleryImage
              image={img0}
              sizes="(min-width: 768px) 50vw, 100vw"
              className="h-70 md:h-auto"
            />
            <GalleryImage
              image={img1}
              sizes="(min-width: 768px) 50vw, 100vw"
              className="h-70 md:h-auto"
            />
          </div>
        )}
        {imgs.length === 3 && (
          <div className="mt-6 grid grid-cols-1 gap-3 md:h-120 md:grid-cols-12 md:grid-rows-2">
            <GalleryImage
              image={img0}
              sizes="(min-width: 768px) 58vw, 100vw"
              className="h-70 md:col-span-7 md:row-span-2 md:h-auto"
            />
            <GalleryImage
              image={img1}
              sizes="(min-width: 768px) 42vw, 100vw"
              className="h-70 md:col-span-5 md:h-auto"
            />
            <GalleryImage
              image={img2}
              sizes="(min-width: 768px) 42vw, 100vw"
              className="h-70 md:col-span-5 md:h-auto"
            />
          </div>
        )}
        {imgs.length >= 4 && (
          <div className="mt-6 grid grid-cols-1 gap-3 md:h-120 md:grid-cols-4 md:grid-rows-2">
            <GalleryImage
              image={img0}
              sizes="(min-width: 768px) 50vw, 100vw"
              className="h-70 md:col-span-2 md:row-span-2 md:h-auto"
            />
            <GalleryImage
              image={img1}
              sizes="(min-width: 768px) 50vw, 100vw"
              className="h-70 md:col-span-2 md:h-auto"
            />
            <GalleryImage
              image={img3}
              sizes="(min-width: 768px) 25vw, 100vw"
              className="h-70 md:h-auto"
            />
            <GalleryImage
              image={img2}
              sizes="(min-width: 768px) 25vw, 100vw"
              className="h-70 md:h-auto"
            />
          </div>
        )}
      </Container>
    </section>
  )
}

export default Images
